import type { ComponentProps } from 'react';
import { Questionnaire as QuestionnairePrimitive } from '@shadcn/react/questionnaire';
import { Check } from 'lucide-react';
import { cn } from '../../lib/utils.js';
import { buttonVariants, type Button } from './BaseButton.js';

/**
 * Ported from shadcn's Questionnaire (https://ui.shadcn.com/docs/components/base/questionnaire),
 * restyled onto this app's design tokens. See TddPage.tsx for the multi-step TDD questionnaire
 * built on top of this.
 */

export function Questionnaire({ className, ...props }: ComponentProps<typeof QuestionnairePrimitive.Root>) {
  return <QuestionnairePrimitive.Root data-slot="questionnaire" className={cn('flex w-full min-w-0 flex-col gap-4', className)} {...props} />;
}

export function QuestionnaireProgress({ className, ...props }: ComponentProps<typeof QuestionnairePrimitive.Progress>) {
  return (
    <QuestionnairePrimitive.Progress
      data-slot="questionnaire-progress"
      className={cn('min-h-[1lh] w-fit min-w-[14ch] text-xs font-medium text-muted-foreground tabular-nums', className)}
      {...props}
    />
  );
}

export function QuestionnaireItem({ className, ...props }: ComponentProps<typeof QuestionnairePrimitive.Item>) {
  return (
    <QuestionnairePrimitive.Item data-slot="questionnaire-item" className={cn('flex min-w-0 flex-col gap-4 border-0 p-0 outline-none', className)} {...props} />
  );
}

export function QuestionnaireTitle({ className, ...props }: ComponentProps<typeof QuestionnairePrimitive.Title>) {
  return (
    <QuestionnairePrimitive.Title
      data-slot="questionnaire-title"
      className={cn('font-heading text-base font-semibold text-pretty [&:not(:has(~[data-slot=questionnaire-description]))]:mb-4', className)}
      {...props}
    />
  );
}

export function QuestionnaireDescription({ className, ...props }: ComponentProps<typeof QuestionnairePrimitive.Description>) {
  return (
    <QuestionnairePrimitive.Description data-slot="questionnaire-description" className={cn('text-xs text-pretty text-muted-foreground', className)} {...props} />
  );
}

export function QuestionnaireChoices({ className, ...props }: ComponentProps<typeof QuestionnairePrimitive.Choices>) {
  return (
    <QuestionnairePrimitive.Choices data-slot="questionnaire-choices" className={cn('group/questionnaire-choices grid min-w-0 gap-2', className)} {...props} />
  );
}

export function QuestionnaireChoice({ children, className, ...props }: ComponentProps<typeof QuestionnairePrimitive.Choice>) {
  return (
    <QuestionnairePrimitive.Choice
      data-slot="questionnaire-choice"
      className={cn(
        'group/questionnaire-choice relative flex min-h-11 cursor-pointer items-start gap-2.5 rounded-lg border border-border bg-transparent px-3 py-2.5 text-start text-sm transition-colors outline-none select-none hover:border-[var(--ink-400)]',
        'has-[>input:focus-visible]:border-focus has-[>input:focus-visible]:ring-2 has-[>input:focus-visible]:ring-focus data-invalid:border-danger',
        'data-checked:border-accent data-checked:bg-accent-soft data-disabled:pointer-events-none data-disabled:cursor-not-allowed data-disabled:opacity-50',
        className,
      )}
      {...props}
    >
      <QuestionnairePrimitive.ChoiceInput data-slot="questionnaire-choice-input" className="absolute inset-0 z-10 size-full cursor-pointer opacity-0" />
      <span
        aria-hidden="true"
        data-slot="questionnaire-choice-indicator"
        className="pointer-events-none relative flex size-4 shrink-0 translate-y-0.5 items-center justify-center rounded-[4px] border border-border group-data-[type=radio]/questionnaire-choice:rounded-full group-data-checked/questionnaire-choice:border-accent group-data-checked/questionnaire-choice:bg-accent group-data-checked/questionnaire-choice:text-accent-fg"
      >
        <span
          data-slot="questionnaire-choice-indicator-dot"
          className="hidden size-2 rounded-full bg-accent-fg group-data-[type=checkbox]/questionnaire-choice:hidden group-data-checked/questionnaire-choice:block"
        />
        <Check data-slot="questionnaire-choice-indicator-check" className="hidden size-3.5 group-data-[type=radio]/questionnaire-choice:hidden group-data-checked/questionnaire-choice:block" />
      </span>
      <QuestionnairePrimitive.ChoiceLabel data-slot="questionnaire-choice-label" className="flex min-w-0 flex-1 flex-col gap-0.5 leading-snug">
        {children}
      </QuestionnairePrimitive.ChoiceLabel>
    </QuestionnairePrimitive.Choice>
  );
}

export function QuestionnaireChoiceDescription({ className, ...props }: ComponentProps<'span'>) {
  return <span data-slot="questionnaire-choice-description" className={cn('text-muted-foreground', className)} {...props} />;
}

export function QuestionnaireInput({ className, ...props }: ComponentProps<typeof QuestionnairePrimitive.Input>) {
  return (
    <div data-slot="questionnaire-input-wrapper" className="group/questionnaire-input relative w-full min-w-0">
      <QuestionnairePrimitive.Input
        data-slot="questionnaire-input"
        className={cn(
          'h-8 min-h-11 w-full min-w-0 rounded-lg border border-border bg-transparent px-2.5 py-1 text-sm outline-none transition-colors placeholder:text-muted-foreground focus-visible:border-focus focus-visible:ring-2 focus-visible:ring-focus sm:min-h-0',
          className,
        )}
        {...props}
      />
    </div>
  );
}

export function QuestionnaireError({ className, ...props }: ComponentProps<typeof QuestionnairePrimitive.Error>) {
  return <QuestionnairePrimitive.Error data-slot="questionnaire-error" className={cn('mt-2 text-sm text-danger', className)} {...props} />;
}

export function QuestionnaireActions({ className, ...props }: ComponentProps<'div'>) {
  return (
    <div data-slot="questionnaire-actions" className={cn('flex min-h-11 w-full flex-wrap items-center justify-end gap-2 sm:min-h-8 [&>:first-child]:mr-auto', className)} {...props} />
  );
}

export function QuestionnairePrevious({
  children,
  className,
  size = 'default',
  variant = 'outline',
  ...props
}: ComponentProps<typeof QuestionnairePrimitive.Previous> & Pick<ComponentProps<typeof Button>, 'size' | 'variant'>) {
  return (
    <QuestionnairePrimitive.Previous
      data-slot="questionnaire-previous"
      className={cn(buttonVariants({ size, variant }), 'col-start-1 row-start-1 min-h-11 justify-self-start sm:min-h-0', className)}
      {...props}
    >
      {children ?? 'Back'}
    </QuestionnairePrimitive.Previous>
  );
}

export function QuestionnaireNext({
  children,
  className,
  size = 'default',
  variant = 'default',
  ...props
}: ComponentProps<typeof QuestionnairePrimitive.Next> & Pick<ComponentProps<typeof Button>, 'size' | 'variant'>) {
  return (
    <QuestionnairePrimitive.Next
      data-slot="questionnaire-next"
      className={cn(buttonVariants({ size, variant }), 'col-start-3 row-start-1 min-h-11 justify-self-end sm:min-h-0', className)}
      {...props}
    >
      {children ?? 'Next'}
    </QuestionnairePrimitive.Next>
  );
}

export function QuestionnaireSubmit({
  children,
  className,
  size = 'default',
  variant = 'default',
  ...props
}: ComponentProps<typeof QuestionnairePrimitive.Submit> & Pick<ComponentProps<typeof Button>, 'size' | 'variant'>) {
  return (
    <QuestionnairePrimitive.Submit
      data-slot="questionnaire-submit"
      className={cn(buttonVariants({ size, variant }), 'col-start-3 row-start-1 min-h-11 justify-self-end sm:min-h-0', className)}
      {...props}
    >
      {children ?? 'Submit'}
    </QuestionnairePrimitive.Submit>
  );
}
